/**
 * Reaksjoner i Chat-rommet (VIB-3025). Tre faste emojier. 👍 på
 * prosjektoppdateringer er en «like» i project_comment_likes, så tallet er
 * det samme på prosjektkortet; resten ligger i message_reactions.
 */

export const REACTION_EMOJIS = ["👍", "🎉", "👀"] as const;
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

export const REACTION_LABELS: Record<ReactionEmoji, string> = {
  "👍": "Tommel opp",
  "🎉": "Feiring",
  "👀": "Ser på det",
};

export interface ReactionRow {
  message_id: string;
  user_id: string;
  emoji: string;
}

export interface ReactionSummary {
  emoji: ReactionEmoji;
  count: number;
  mine: boolean;
  userIds: string[];
}

const isReactionEmoji = (e: string): e is ReactionEmoji => (REACTION_EMOJIS as readonly string[]).includes(e);

/**
 * Teller reaksjoner per melding i fast rekkefølge. `likesByMessage` er 👍 fra
 * project_comment_likes for prosjektoppdateringer. Bare emojier med minst én
 * reaksjon tas med.
 */
export function summarizeReactions(
  rows: ReactionRow[],
  likesByMessage: ReadonlyMap<string, readonly string[]>,
  selfId: string | null,
): Map<string, ReactionSummary[]> {
  const users = new Map<string, Map<ReactionEmoji, Set<string>>>();
  const add = (messageId: string, emoji: ReactionEmoji, userId: string) => {
    const perMessage = users.get(messageId) ?? new Map<ReactionEmoji, Set<string>>();
    const set = perMessage.get(emoji) ?? new Set<string>();
    set.add(userId);
    perMessage.set(emoji, set);
    users.set(messageId, perMessage);
  };
  for (const r of rows) if (isReactionEmoji(r.emoji)) add(r.message_id, r.emoji, r.user_id);
  for (const [messageId, likers] of likesByMessage) for (const u of likers) add(messageId, "👍", u);

  const result = new Map<string, ReactionSummary[]>();
  for (const [messageId, perMessage] of users) {
    result.set(
      messageId,
      REACTION_EMOJIS.filter((e) => (perMessage.get(e)?.size ?? 0) > 0).map((emoji) => {
        const ids = [...(perMessage.get(emoji) ?? [])];
        return { emoji, count: ids.length, mine: !!selfId && ids.includes(selfId), userIds: ids };
      }),
    );
  }
  return result;
}
